import axios, { Method, AxiosRequestHeaders } from 'axios'
import { auth } from 'firebase-config'
import { env } from 'services/helpers/env'
import {
  isE2ETestMode,
  mockCustomers,
  mockGlobalZones,
  mockGlobalDataUsage,
  mockLookupUsage,
  mockNordicLookupsUsage,
  mockNordicLookupWindows,
  mockRisikaUsers,
  mockCustomerData,
  mockEscalations,
} from 'services/helpers/mock-data'

interface CallCmAPIRequest {
  method?: Method
  data?: unknown
  headers?: AxiosRequestHeaders
}

const getAccessToken = async (): Promise<string> => {
  if (!auth.currentUser) {
    throw new Error('No authenticated user')
  }
  return auth.currentUser.getIdToken()
}

// Mock API responses for E2E testing
const getMockResponse = (endpoint: string, method: string) => {
  // GET /customer - list all customers
  if (endpoint === '/customer' && method === 'get') {
    return { data: { data: mockCustomers } }
  }
  // GET /customer/:id - get single customer
  if (endpoint.match(/^\/customer\/\d+$/) && method === 'get') {
    return { data: { data: mockCustomerData } }
  }
  // GET /global-data/get-all-zones - list zones
  if (endpoint === '/global-data/get-all-zones' && method === 'get') {
    return { data: mockGlobalZones }
  }
  // GET /global-data/usage - global data usage across all customers.
  // Emulates the server-side search + pagination contract: q filters on
  // name/local_id across the full set, then the page is sliced from the
  // filtered result. Companies in the fixture are pre-sorted by allocation
  // (descending), matching the server's sort order.
  if (endpoint.startsWith('/global-data/usage') && method === 'get') {
    const params = new URLSearchParams(endpoint.split('?')[1] || '')
    const page = parseInt(params.get('page') || '1', 10)
    const pageSize = parseInt(params.get('page_size') || '10', 10)
    const q = (params.get('q') || '').toLowerCase()
    const filtered = q
      ? mockGlobalDataUsage.companies.filter(
          (company) =>
            company.name.toLowerCase().includes(q) ||
            company.local_id.toLowerCase().includes(q),
        )
      : mockGlobalDataUsage.companies
    const start = (page - 1) * pageSize
    return {
      data: {
        companies: filtered.slice(start, start + pageSize),
        totals: mockGlobalDataUsage.totals,
        pagination: {
          page,
          page_size: pageSize,
          total: filtered.length,
          pages: Math.max(1, Math.ceil(filtered.length / pageSize)),
        },
      },
    }
  }
  // GET /lookup-usage/:id - a customer's lookups from the trail (ENG2-2849).
  // Every mocked customer gets the same answer for the period it asks for.
  const lookupUsageMatch = endpoint.match(/^\/lookup-usage\/(\d+)/)
  if (lookupUsageMatch && method === 'get') {
    const params = new URLSearchParams(endpoint.split('?')[1] || '')
    const month = params.get('month')
    return {
      data: {
        ...mockLookupUsage,
        company_id: Number(lookupUsageMatch[1]),
        year: Number(params.get('year') ?? mockLookupUsage.year),
        month: month === null ? null : Number(month),
      },
    }
  }
  // GET /nordic-lookups/usage/:id/lookups - companies counted for one customer
  // this period (ENG2-2465). Every mocked customer gets the same list.
  const windowsMatch = endpoint.match(
    /^\/nordic-lookups\/usage\/(\d+)\/lookups/,
  )
  if (windowsMatch && method === 'get') {
    const params = new URLSearchParams(endpoint.split('?')[1] || '')
    const page = parseInt(params.get('page') || '1', 10)
    const pageSize = parseInt(params.get('page_size') || '10', 10)
    const q = (params.get('q') || '').toLowerCase()
    const company = mockNordicLookupsUsage.companies.find(
      (c) => c.company_id === Number(windowsMatch[1]),
    )
    const filtered = mockNordicLookupWindows.filter((w) =>
      w.local_id.toLowerCase().includes(q),
    )
    const start = (page - 1) * pageSize
    return {
      data: {
        company: company ?? mockNordicLookupsUsage.companies[0],
        lookups: filtered.slice(start, start + pageSize),
        pagination: {
          page,
          page_size: pageSize,
          total: filtered.length,
          pages: Math.max(1, Math.ceil(filtered.length / pageSize)),
        },
      },
    }
  }
  // GET /nordic-lookups/usage - Nordic lookup usage across the 2026-package
  // customers (ENG2-2465). Search + slice; the fixture is pre-sorted.
  if (endpoint.startsWith('/nordic-lookups/usage') && method === 'get') {
    const params = new URLSearchParams(endpoint.split('?')[1] || '')
    const page = parseInt(params.get('page') || '1', 10)
    const pageSize = parseInt(params.get('page_size') || '10', 10)
    const q = (params.get('q') || '').toLowerCase()
    const filtered = mockNordicLookupsUsage.companies.filter(
      (company) =>
        company.name.toLowerCase().includes(q) ||
        company.local_id.toLowerCase().includes(q),
    )
    const start = (page - 1) * pageSize
    return {
      data: {
        companies: filtered.slice(start, start + pageSize),
        totals: mockNordicLookupsUsage.totals,
        pagination: {
          page,
          page_size: pageSize,
          total: filtered.length,
          pages: Math.max(1, Math.ceil(filtered.length / pageSize)),
        },
      },
    }
  }
  // GET /escalation - the escalations queue across every customer. Emulates the
  // server-side filter + slice contract: countries/status/q narrow the full set,
  // then the page is sliced from what is left. available_countries is computed
  // from the WHOLE set, not the filtered one, because the country dropdown binds
  // to it and must not collapse to whatever is currently selected.
  if (endpoint.startsWith('/escalation') && method === 'get') {
    const params = new URLSearchParams(endpoint.split('?')[1] || '')
    const page = parseInt(params.get('page') || '1', 10)
    const pageSize = parseInt(params.get('page_size') || '20', 10)
    const countries = (params.get('countries') || '').split(',').filter(Boolean)
    const statuses = (params.get('status') || '').split(',').filter(Boolean)
    const q = (params.get('q') || '').toLowerCase()
    const localId = params.get('local_id') || ''

    const filtered = mockEscalations.filter((escalation) => {
      if (countries.length && !countries.includes(escalation.country))
        return false
      if (statuses.length && !statuses.includes(escalation.status)) return false
      if (localId && escalation.local_id !== localId) return false
      if (q && !escalation.local_id.toLowerCase().includes(q)) return false
      return true
    })
    const start = (page - 1) * pageSize

    return {
      data: {
        escalations: filtered.slice(start, start + pageSize),
        available_countries: Array.from(
          new Set(mockEscalations.map((escalation) => escalation.country)),
        ).sort(),
        pagination: {
          page,
          page_size: pageSize,
          total: filtered.length,
          pages: Math.max(1, Math.ceil(filtered.length / pageSize)),
        },
      },
    }
  }
  // POST /escalation/update-escalation - record a missing-financials reason
  if (endpoint === '/escalation/update-escalation' && method === 'post') {
    return { data: { status: 'REASON_RECORDED' } }
  }
  // POST /global-data/create-zone - create a custom zone
  if (endpoint === '/global-data/create-zone' && method === 'post') {
    return {
      data: { zone_id: 999, name: 'Custom E2E Zone', countries: ['DK', 'SE'] },
    }
  }
  // PUT /global-data/update-zone - update a custom zone
  if (endpoint === '/global-data/update-zone' && method === 'put') {
    return {
      data: {
        zone_id: 1,
        name: 'Nordic Region',
        countries: ['DK', 'SE', 'NO', 'FI'],
      },
    }
  }
  // GET /user/risika - list risika users
  if (endpoint === '/user/risika' && method === 'get') {
    return { data: { data: mockRisikaUsers } }
  }
  // Default: return empty data
  return { data: { data: [] } }
}

const callCmAPI = async (endpoint: string, request: CallCmAPIRequest = {}) => {
  const { method = 'get', data, headers } = request

  // In E2E test mode, return mock data
  if (isE2ETestMode()) {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 100))
    return getMockResponse(endpoint, method.toLowerCase())
  }

  const url = `${env.WEB_API_BASE_URL}${endpoint}`
  const accessToken = await getAccessToken()
  return axios({
    url,
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      ...headers,
    },
    data,
  })
}

export default callCmAPI
